import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { IImageInfo } from "@types";
import { t } from "i18next";
import { RefreshCw } from "lucide-react";
import { clearImgData, getImgData, sendImgDataFromStorage, updateAIModules } from "common-lib";
import { getInspStatusImage } from "../../services/inspectService";
import { getLatestInspResult } from "../../services/inspLogService";
import { useRecoilValue } from "recoil";
import { heatmapModelState, heatmapModuleMsgState, heatmapModuleState } from "../../recoil/upgrade";
import { useHeatmapModuleUser } from "../../hooks/useHeatmapModel";
import ScoremapOverlay from "./ScoremapOverlay";
import { ScoremapOverlayProps } from "../new/ImagePart";
import { pickSnapshot, scoreHistogram } from "./snapshotPick";
import { formatThreshold } from "../../utils/formatThreshold";

const Overlay = ScoremapOverlay as React.FC<ScoremapOverlayProps>;

interface TrainSnapshotProps {
  projectId: number;
  serverAddress?: string;
  count?: number;
}

type HeatmapEntry = { base: string; maps: string[] } | { error: string };

// 분포 그래프 x축 눈금 (0.0 ~ 1.0)
const TICKS = Array.from({ length: 11 }, (_, i) => i / 10);

export default function TrainSnapshot({ projectId, serverAddress, count = 8 }: TrainSnapshotProps) {
  const [dataset, setDataset] = useState<IImageInfo[]>([]);
  const [threshold, setThreshold] = useState(0);
  const [heatmapMsg, setHeatmapMsg] = useState<string | null>(null);
  const [site, setSite] = useState<string | null>(null);
  const [origins, setOrigins] = useState<Record<string, string>>({});
  const [heatmaps, setHeatmaps] = useState<Record<string, HeatmapEntry>>({});
  const [loaded, setLoaded] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  const heatmapModel = useRecoilValue(heatmapModelState);
  const modelUuid = heatmapModel?.uuid ?? "";
  useHeatmapModuleUser(true);
  const loadedModule = useRecoilValue(heatmapModuleState);
  const moduleMsg = useRecoilValue(heatmapModuleMsgState);
  const module = loadedModule?.modelUuid === modelUuid ? loadedModule : null;

  const { ok, ng } = useMemo(() => pickSnapshot(dataset, count), [dataset, count]);
  const picks = useMemo(() => [...ok, ...ng], [ok, ng]);
  const hist = useMemo(() => scoreHistogram(dataset), [dataset]);

  useEffect(() => {
    fetch("/config.json")
      .then((res) => res.json())
      .then((config) => setSite(config.SITE_OPTION))
      .catch(console.error);
  }, []);

  // 선택한 모델의 검증 결과 + 그때 쓴 threshold
  useEffect(() => {
    let cancelled = false;
    setDataset([]);

    (async () => {
      const res = await getLatestInspResult(projectId, modelUuid);
      if (cancelled) return;
      setDataset(res?.results ?? []);
      setThreshold(Number(res?.threshold ?? 0));
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, modelUuid]);

  // 원본: 파일 서버에서 바로 받는다 (AI 서버와 무관하게 먼저 뜬다)
  useEffect(() => {
    let cancelled = false;
    const urls: string[] = [];

    (async () => {
      const entries = await Promise.all(
        picks.map(async (item) => [item.path, await getInspStatusImage(item.path)] as [string, string | undefined]),
      );
      const loadedUrls: Record<string, string> = {};
      entries.forEach(([path, url]) => {
        if (!url) return;
        urls.push(url);
        loadedUrls[path] = url;
      });
      if (cancelled) return;
      setOrigins(loadedUrls);
    })();

    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
      setOrigins({});
    };
  }, [picks]);

  // 히트맵: AI 서버는 spec 하나로 한 장씩만 주고받으므로 순차 요청한다
  useEffect(() => {
    if (!module || !site || picks.length === 0) {
      return;
    }

    const { specId, serverAddress: ip } = module;
    let cancelled = false;
    setHeatmaps({});
    setHeatmapMsg(null);
    setLoaded(0);

    (async () => {
      try {
        await updateAIModules(
          ip,
          { train_id: specId, site, type: "scoremap", total: 0, trained_count: 0, is_complete: false },
          module,
        );
        for (const item of picks) {
          if (cancelled) return;
          try {
            await sendImgDataFromStorage(ip, {
              uuid: specId,
              path: item.path,
              priority: 0,
              destination: { index1: "tag", index2: "image_bytes_original", index3: "scoremap" },
            });
            const res = await getImgData(ip, specId, false, "scoremap");
            // scoremap은 여러 장이 와도 화면에 쓰는 건 마지막 한 장이다 (ImagePart와 동일)
            const maps = Array.isArray(res?.scoremap) ? res.scoremap.slice(-1) : res?.scoremap ? [res.scoremap] : [];
            if (cancelled) return;
            setHeatmaps((prev) => ({
              ...prev,
              [item.path]:
                maps.length && res?.originImage
                  ? { base: res.originImage, maps }
                  : { error: res?.message ?? t("NoImage") },
            }));
          } catch (error: any) {
            if (cancelled) return;
            setHeatmaps((prev) => ({ ...prev, [item.path]: { error: error?.message ?? t("NoImage") } }));
          } finally {
            await clearImgData(ip, specId).catch(() => {});
            if (!cancelled) setLoaded((n) => n + 1);
          }
        }
      } catch (error: any) {
        if (!cancelled) setHeatmapMsg(error?.message ?? t("NoImage"));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [module, site, picks, reloadKey]);

  // 가운데 분포: 위쪽 파랑 = OK, 아래쪽 빨강 = NG, 점선 = threshold
  // ponytail: chart.js 대신 div 막대. 드래그/축이 필요하면 TrainHistogram을 쓴다
  const barPct = (n: number) => (n ? Math.max(4, (Math.sqrt(n) / Math.sqrt(hist.max)) * 100) : 0);
  const thrPct = Math.min(1, Math.max(0, threshold)) * 100;

  const renderHistogram = () => (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "stretch",
          height: 120,
          gap: "1px",
          // 0.1 단위 세로 보조선
          backgroundImage: "repeating-linear-gradient(to right, var(--dk-border, #eef0f3) 0 1px, transparent 1px 10%)",
        }}
      >
        {hist.ok.map((okCount, i) => (
          <Box
            key={i}
            title={`${(i / hist.ok.length).toFixed(2)} ~ ${((i + 1) / hist.ok.length).toFixed(2)} · OK ${okCount} / NG ${hist.ng[i]}`}
            sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}
          >
            <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end" }}>
              <Box sx={{ width: "100%", height: `${barPct(okCount)}%`, backgroundColor: "#3b82f6" }} />
            </Box>
            <Box sx={{ flex: 1, borderTop: "1px solid var(--dk-border, #e5e7eb)" }}>
              <Box sx={{ width: "100%", height: `${barPct(hist.ng[i])}%`, backgroundColor: "#ef4444" }} />
            </Box>
          </Box>
        ))}
        <Box sx={{ position: "absolute", top: 0, bottom: 0, left: `${thrPct}%`, borderLeft: "2px dashed #e91e63" }}>
          <Typography
            sx={{
              position: "absolute",
              top: 2,
              [thrPct > 50 ? "right" : "left"]: 4,
              whiteSpace: "nowrap",
              fontSize: 10,
              fontWeight: 700,
              color: "#e91e63",
            }}
          >
            {t("Threshold")} {formatThreshold(threshold)}
          </Typography>
        </Box>
      </Box>
      {/* 눈금: 막대만 보고 점수를 가늠할 수 있게 0.1 단위 표기 */}
      <Box sx={{ position: "relative", height: 18, mt: 0.25 }}>
        {TICKS.map((v) => (
          <Box
            key={v}
            sx={{
              position: "absolute",
              left: `${v * 100}%`,
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "1px", height: 4, backgroundColor: "var(--dk-border, #d1d5db)" }} />
            <Typography sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1.4 }}>{v.toFixed(1)}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderRow = (items: IImageInfo[], kind: "OK" | "NG") => (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5, color: kind === "OK" ? "primary.main" : "error.main" }}>
        {kind === "OK" ? t("OKImage") : t("NGImage")} ({items.length})
      </Typography>
      {items.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t("NoDataMsg")}</Typography>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${count}, minmax(96px, 1fr))`, gap: 1 }}>
          {items.map((item) => {
            const score = Number(item.score);
            // OK인데 threshold 이상 = 과검, NG인데 threshold 미만 = 미검
            const risky = kind === "OK" ? score >= threshold : score < threshold;
            const heat = heatmaps[item.path];
            return (
              <Box key={item.path} sx={{ minWidth: 0 }}>
                <Tooltip title={item.path} arrow>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      textAlign: "center",
                      color: risky ? (kind === "OK" ? "error.main" : "warning.main") : "text.secondary",
                    }}
                  >
                    {score.toFixed(4)}
                    {risky && ` · ${kind === "OK" ? t("Overkill") : t("Missing")}`}
                  </Typography>
                </Tooltip>
                <Box sx={cellSx}>
                  {origins[item.path] ? (
                    <img src={origins[item.path]} alt="" style={imgStyle} />
                  ) : (
                    <CircularProgress size={16} />
                  )}
                </Box>
                <Box sx={{ ...cellSx, mt: 0.5 }}>
                  {heat && "maps" in heat ? (
                    <Overlay
                      scoremapImages={heat.maps}
                      baseImage={heat.base}
                      blend="max"
                      opacity={0.5}
                      enableZoom={false}
                      zoomMode="off"
                    />
                  ) : heat ? (
                    <Typography sx={{ fontSize: 10, color: "text.secondary", textAlign: "center", px: 0.5 }}>
                      {heat.error}
                    </Typography>
                  ) : (
                    <CircularProgress size={16} />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {t("Threshold")}: [{formatThreshold(threshold)}] · {t("Heatmap")} {loaded}/{picks.length}
        </Typography>
        <Tooltip title={t("SnapshotTooltipMsg")} arrow>
          <span>
            <IconButton
              size="small"
              disabled={!module || loaded < picks.length}
              onClick={() => setReloadKey((n) => n + 1)}
            >
              <RefreshCw size={16} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {renderRow(ok, "OK")}
      {renderHistogram()}
      {renderRow(ng, "NG")}
      {!serverAddress ? (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t("NoConnectedModuleMsg")}</Typography>
      ) : (
        (heatmapMsg ?? moduleMsg) && (
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{heatmapMsg ?? moduleMsg}</Typography>
        )
      )}
    </Box>
  );
}

const cellSx = {
  height: 96,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  borderRadius: 1,
  border: "1px solid var(--dk-border, #e5e7eb)",
  backgroundColor: "var(--dk-surface-2, #fafafa)",
};

const imgStyle: React.CSSProperties = { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" };
